import { useEffect, useRef } from 'preact/hooks';
import { signal, computed } from '@preact/signals';
import { api } from '../api/client';
import { SkeletonBar, Button, Spinner, Input, Select, Textarea } from '../components/ui';
import { T } from '../styles/tokens';

// ══════════════════════════════════════════════════════════════════════════
// CSS KEYFRAMES (injected once)
// ══════════════════════════════════════════════════════════════════════════

const STYLE_ID = 'admin-cerebros-styles';
function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes cerebro-fade-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes cerebro-slide-down { from { opacity: 0; max-height: 0; transform: translateY(-10px); } to { opacity: 1; max-height: 600px; transform: translateY(0); } }
    @keyframes cerebro-pulse { 0%, 100% { opacity: 0.15; } 50% { opacity: 0.25; } }
    @keyframes spin { to { transform: rotate(360deg); } }
    @media (max-width: 640px) {
      .cerebro-card-actions { flex-direction: column !important; }
      .cerebro-card-actions button { width: 100% !important; }
      .cerebro-grid-2 { grid-template-columns: 1fr !important; }
      .cerebro-header-row { flex-direction: column !important; gap: 12px !important; align-items: stretch !important; }
      .cerebro-header-row button { width: 100% !important; }
      .cerebro-filter-row { flex-direction: column !important; gap: 8px !important; }
      .cerebro-filter-row > * { width: 100% !important; }
      .cerebro-edit-footer-actions { flex-direction: column !important; }
      .cerebro-edit-footer-actions button { width: 100% !important; }
    }
  `;
  document.head.appendChild(style);
}

// ══════════════════════════════════════════════════════════════════════════
// TYPES
// ══════════════════════════════════════════════════════════════════════════

interface CerebroFull {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  avatar_url: string | null;
  emoji?: string | null;
  category?: string | null;
  config_json: string;
  config?: CerebroConfig;
  enabled: number;
  created_at: string;
  updated_at: string;
}

interface CerebroConfig {
  agentName?: string;
  agents: string;
  soul: string;
  tools: string;
  identity: string;
  user: string;
  heartbeat: string;
  bootstrap: string;
  memory: string;
}

const CONFIG_FIELDS: (keyof CerebroConfig)[] = [
  'agents', 'soul', 'tools', 'identity', 'user', 'heartbeat', 'bootstrap', 'memory',
];

const CONFIG_LABELS: Record<string, { emoji: string; label: string }> = {
 agents: { emoji: '', label: 'Agents' },
 soul: { emoji: '', label: 'Soul' },
 tools: { emoji: '', label: 'Tools' },
 identity: { emoji: '', label: 'Identity' },
 user: { emoji: '', label: 'User' },
 heartbeat: { emoji: '', label: 'Heartbeat' },
 bootstrap: { emoji: '', label: 'Bootstrap' },
 memory: { emoji: '', label: 'Memory' },
};

const CATEGORIES = ['Assistente', 'Especialista', 'Criativo', 'Técnico', 'Outro'] as const;

const EMOJI_SUGGESTIONS = ['', '', '', '', '', '', '', '', '', '', '', '', '', '', ''];

// ══════════════════════════════════════════════════════════════════════════
// STATE
// ══════════════════════════════════════════════════════════════════════════

const cerebros = signal<CerebroFull[]>([]);
const loading = signal(true);
const error = signal('');
const successMsg = signal('');
const editing = signal<CerebroFull | null>(null);
const editConfig = signal<CerebroConfig | null>(null);
const editOriginal = signal<string>(''); // JSON snapshot for dirty check
const activeTab = signal<keyof CerebroConfig>('agents');
const saving = signal(false);
const showCreate = signal(false);
const newName = signal('');
const newSlug = signal('');
const newDesc = signal('');
const newEmoji = signal('');
const newCategory = signal('Outro');
const creating = signal(false);

// Filters, search, pagination
const filterStatus = signal<'all' | 'active' | 'inactive'>('all');
const searchQuery = signal('');
const currentPage = signal(1);
const PAGE_SIZE = 20;

// Validation
const validationErrors = signal<Record<string, string>>({});

// Computed: filtered + paginated list
const filteredCerebros = computed(() => {
  let list = cerebros.value;
  // Status filter
  if (filterStatus.value === 'active') list = list.filter(c => c.enabled);
  if (filterStatus.value === 'inactive') list = list.filter(c => !c.enabled);
  // Search
  const q = searchQuery.value.toLowerCase().trim();
  if (q) list = list.filter(c => c.name.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q));
  return list;
});

const totalPages = computed(() => Math.max(1, Math.ceil(filteredCerebros.value.length / PAGE_SIZE)));

const paginatedCerebros = computed(() => {
  const start = (currentPage.value - 1) * PAGE_SIZE;
  return filteredCerebros.value.slice(start, start + PAGE_SIZE);
});

// ══════════════════════════════════════════════════════════════════════════
// HELPERS
// ══════════════════════════════════════════════════════════════════════════

function getEditSnapshot(): string {
  if (!editing.value || !editConfig.value) return '';
  return JSON.stringify({ name: editing.value.name, slug: editing.value.slug, description: editing.value.description, emoji: editing.value.emoji, category: editing.value.category, config: editConfig.value, enabled: editing.value.enabled });
}

function isDirty(): boolean {
  return editOriginal.value !== getEditSnapshot();
}

function validateCreate(): boolean {
  const errs: Record<string, string> = {};
  if (!newName.value.trim()) errs.name = 'Nome é obrigatório';
  if (!newSlug.value.trim()) errs.slug = 'Slug é obrigatório';
  if (newDesc.value.trim().length > 0 && newDesc.value.trim().length < 10) errs.desc = 'Descrição deve ter pelo menos 10 caracteres';
  // Check slug unique
  if (newSlug.value && cerebros.value.some(c => c.slug === newSlug.value.trim())) errs.slug = 'Slug já existe';
  validationErrors.value = errs;
  return Object.keys(errs).length === 0;
}

function validateEdit(): boolean {
  if (!editing.value) return false;
  const errs: Record<string, string> = {};
  if (!editing.value.name.trim()) errs.name = 'Nome é obrigatório';
  if (!editing.value.slug.trim()) errs.slug = 'Slug é obrigatório';
  if (editing.value.description && editing.value.description.trim().length > 0 && editing.value.description.trim().length < 10) errs.desc = 'Descrição deve ter pelo menos 10 caracteres';
  // Slug unique (excluding self)
  if (editing.value.slug && cerebros.value.some(c => c.slug === editing.value!.slug.trim() && c.id !== editing.value!.id)) errs.slug = 'Slug já existe';
  validationErrors.value = errs;
  return Object.keys(errs).length === 0;
}

// ══════════════════════════════════════════════════════════════════════════
// FETCHERS
// ══════════════════════════════════════════════════════════════════════════

async function fetchCerebros() {
  loading.value = true;
  error.value = '';
  try {
    const data = await api.get<{ cerebros: CerebroFull[] }>('/cloud/admin/cerebros');
    cerebros.value = data.cerebros;
  } catch (e: any) {
    error.value = e.message || 'Falha ao carregar cerebros';
  } finally {
    loading.value = false;
  }
}

async function loadCerebroDetail(id: string) {
  try {
    const data = await api.get<{ cerebro: CerebroFull & { config: CerebroConfig } }>(`/cloud/admin/cerebros/${id}`);
    editing.value = data.cerebro;
    editConfig.value = data.cerebro.config || {
      agentName: '', agents: '', soul: '', tools: '', identity: '', user: '', heartbeat: '', bootstrap: '', memory: '',
    };
    activeTab.value = 'agents';
    validationErrors.value = {};
    // Set original snapshot for dirty check (after next tick so signals settle)
    setTimeout(() => { editOriginal.value = getEditSnapshot(); }, 0);
  } catch (e: any) {
    error.value = e.message || 'Falha ao carregar cerebro';
  }
}

async function saveCerebro() {
  if (!editing.value || !editConfig.value) return;
  if (!validateEdit()) return;
  saving.value = true;
  error.value = '';
  try {
    await api.put(`/cloud/admin/cerebros/${editing.value.id}`, {
      name: editing.value.name,
      slug: editing.value.slug,
      description: editing.value.description,
      avatar_url: editing.value.avatar_url,
      emoji: editing.value.emoji,
      category: editing.value.category,
      config: editConfig.value,
      enabled: editing.value.enabled,
    });
    successMsg.value = 'Cerebro salvo com sucesso!';
    editOriginal.value = getEditSnapshot(); // Reset dirty
    setTimeout(() => successMsg.value = '', 3000);
    await fetchCerebros();
  } catch (e: any) {
    error.value = e.message || 'Falha ao salvar';
  } finally {
    saving.value = false;
  }
}

async function createCerebro() {
  if (!validateCreate()) return;
  creating.value = true;
  error.value = '';
  try {
    await api.post('/cloud/admin/cerebros', {
      name: newName.value,
      slug: newSlug.value,
      description: newDesc.value,
      emoji: newEmoji.value,
      category: newCategory.value,
      config: { agentName: '', agents: '', soul: '', tools: '', identity: '', user: '', heartbeat: '', bootstrap: '', memory: '' },
    });
    newName.value = '';
    newSlug.value = '';
    newDesc.value = '';
 newEmoji.value = '';
    newCategory.value = 'Outro';
    showCreate.value = false;
    validationErrors.value = {};
    successMsg.value = 'Cerebro criado!';
    setTimeout(() => successMsg.value = '', 3000);
    await fetchCerebros();
  } catch (e: any) {
    error.value = e.message || 'Falha ao criar';
  } finally {
    creating.value = false;
  }
}

async function deleteCerebro(id: string) {
  if (!confirm('Tem certeza que deseja deletar este cerebro?')) return;
  try {
    await api.delete(`/cloud/admin/cerebros/${id}`);
    successMsg.value = 'Cerebro deletado!';
    setTimeout(() => successMsg.value = '', 3000);
    if (editing.value?.id === id) { editing.value = null; editConfig.value = null; }
    await fetchCerebros();
  } catch (e: any) {
    error.value = e.message || 'Falha ao deletar';
  }
}

async function toggleEnabled(c: CerebroFull) {
  try {
    await api.put(`/cloud/admin/cerebros/${c.id}`, { enabled: c.enabled ? false : true });
    await fetchCerebros();
  } catch (e: any) {
    error.value = e.message || 'Falha ao atualizar';
  }
}

// ══════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ══════════════════════════════════════════════════════════════════════════

function SkeletonCards() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {[0, 1, 2].map(i => (
        <div
          key={i}
          style={{
            background: T.bgCard, border: `1px solid ${T.border}`,
            borderRadius: 14, padding: 18, display: 'flex', alignItems: 'center', gap: 16,
            animation: 'cerebro-pulse 1.5s ease-in-out infinite',
            animationDelay: `${i * 0.2}s`,
          }}
        >
          <div style={{ width: 48, height: 48, borderRadius: 12, background: T.purpleBg, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ height: 16, width: '40%', background: T.bgHover, borderRadius: 6, marginBottom: 8 }} />
            <div style={{ height: 12, width: '60%', background: T.bgCardHover, borderRadius: 4, marginBottom: 6 }} />
            <div style={{ height: 12, width: '80%', background: T.bgCard, borderRadius: 4 }} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ width: 70, height: 28, borderRadius: 8, background: T.bgCardHover }} />
            <div style={{ width: 60, height: 28, borderRadius: 8, background: T.bgCardHover }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ValidationMsg({ field }: { field: string }) {
  const msg = validationErrors.value[field];
  if (!msg) return null;
  return <div style={{ fontSize: 11, color: T.danger, marginTop: 3 }}>{msg}</div>;
}

function Pagination() {
  if (totalPages.value <= 1) return null;
  const pages: number[] = [];
  for (let i = 1; i <= totalPages.value; i++) pages.push(i);
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 16 }}>
      {pages.map(p => (
        <Button variant="ghost"
          key={p}
          onClick={() => currentPage.value = p}
          style={{
            padding: '6px 12px', borderRadius: 8, border: 'none',
            background: currentPage.value === p ? T.primaryBg : T.bgCard,
            color: currentPage.value === p ? T.primary : T.textMuted,
            fontWeight: currentPage.value === p ? 700 : 500,
            fontSize: 13, cursor: 'pointer',
          }}
        >{p}</Button>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ══════════════════════════════════════════════════════════════════════════

const INPUT_STYLE = {
  width: '100%', padding: '10px 14px', borderRadius: 8,
  background: T.blackAlpha30, border: `1px solid ${T.borderHover}`,
  color: T.text, fontSize: 14, outline: 'none',
} as const;

export function AdminCerebrosPage() {
  const createNameRef = useRef<HTMLInputElement>(null);

  useEffect(() => { injectStyles(); fetchCerebros(); }, []);

  // Auto-focus create form
  useEffect(() => {
    if (showCreate.value && createNameRef.current) {
      setTimeout(() => createNameRef.current?.focus(), 50);
    }
  }, [showCreate.value]);

  // Reset page when filters change
  useEffect(() => { currentPage.value = 1; }, [filterStatus.value, searchQuery.value]);

  // ── Skeleton loading ──
  if (loading.value) return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <div style={{ marginBottom: 24 }}>
 <h1 style={{ fontSize: 24, fontWeight: 800, color: T.text, margin: 0 }}>Admin — Cerebros</h1>
        <div style={{ padding: 12 }}><SkeletonBar width="60%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="40%" height={14} /></div>
      </div>
      <SkeletonCards />
    </div>
  );

  // ── Editing mode ──
  if (editing.value && editConfig.value) {
    const c = editing.value;
    const cfg = editConfig.value;
 const emoji = c.emoji || '';
    const hasChanges = isDirty();

    function goBack() {
      if (isDirty()) {
        if (!confirm('Você tem alterações não salvas. Deseja sair sem salvar?')) return;
      }
      editing.value = null;
      editConfig.value = null;
      editOriginal.value = '';
      validationErrors.value = {};
    }

    return (
      <div style={{
        display: 'flex', flexDirection: 'column',
        height: '100vh', overflow: 'hidden',
        animation: 'cerebro-fade-in 0.3s ease',
      }}>
        {/* ─── HEADER (flex-shrink: 0) ─── */}
        <div style={{
          flexShrink: 0, padding: '16px 24px',
          borderBottom: `1px solid ${T.bgHover}`,
          background: T.bgCard,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <Button variant="ghost" size="sm" onClick={goBack}>← Voltar</Button>
            {hasChanges && (
              <div style={{
                padding: '6px 14px', borderRadius: 8,
                background: 'rgba(239, 193, 48, 0.12)', border: '1px solid rgba(239, 193, 48, 0.35)',
                fontSize: 12, fontWeight: 700, color: T.warning,
                display: 'flex', alignItems: 'center', gap: 6,
                animation: 'cerebro-pulse 2s ease-in-out infinite',
              }}>
 Alterações não salvas
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 12,
              background: `linear-gradient(135deg, ${T.purple}, ${T.accent})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
            }}>{emoji}</div>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 800, color: T.text, margin: 0 }}>{c.name}</h1>
              <div style={{ fontSize: 12, color: T.textMuted }}>
 {c.slug} • {c.enabled ? 'Ativo' : 'Desativado'}
                {c.category && <span> • {c.category}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* ─── CONTENT (scrollável) ─── */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: 24,
        }}>
          {/* Meta fields */}
          <div style={{
            background: T.bgCard, border: `1px solid ${T.border}`,
            borderRadius: 14, padding: 20, marginBottom: 20,
          }}>
 <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 14 }}>Informações Básicas</div>
            <div className="cerebro-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: T.textMuted, display: 'block', marginBottom: 4 }}>Nome *</label>
                <Input
                  value={c.name}
                  onChange={(v) => editing.value = { ...c, name: v }}
                  style={INPUT_STYLE}
                />
                <ValidationMsg field="name" />
              </div>
              <div>
                <label style={{ fontSize: 12, color: T.textMuted, display: 'block', marginBottom: 4 }}>Slug *</label>
                <Input
                  value={c.slug}
                  onChange={(v) => editing.value = { ...c, slug: v }}
                  style={INPUT_STYLE}
                />
                <ValidationMsg field="slug" />
              </div>
            </div>
            <div className="cerebro-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: T.textMuted, display: 'block', marginBottom: 4 }}>Emoji</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    value={c.emoji || ''}
                    onInput={(e: any) => editing.value = { ...c, emoji: e.target.value }}
 placeholder=""
                    maxLength={4}
                    style={{ ...INPUT_STYLE, width: 60, textAlign: 'center', fontSize: 20 }}
                  />
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {EMOJI_SUGGESTIONS.slice(0, 8).map(em => (
                      <Button variant="ghost"
                        key={em}
                        onClick={() => editing.value = { ...c, emoji: em }}
                        style={{
                          width: 28, height: 28, borderRadius: 6, border: 'none',
 background: (c.emoji || '') === em ? T.primaryBg : T.bgCard,
                          cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >{em}</Button>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, color: T.textMuted, display: 'block', marginBottom: 4 }}>Categoria</label>
                <Select
                  value={c.category || 'Outro'}
                  onChange={(v) => editing.value = { ...c, category: v }}
                  options={CATEGORIES.map(cat => ({ value: cat, label: cat }))}
                  style={{ ...INPUT_STYLE, cursor: 'pointer' }}
                />
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 12, color: T.textMuted, display: 'block', marginBottom: 4 }}>Descrição</label>
              <Textarea
                value={c.description || ''}
                onInput={(e: any) => editing.value = { ...c, description: e.target.value }}
                rows={2}
                style={{
                  ...INPUT_STYLE, fontSize: 13, resize: 'vertical',
                }}
              />
              <ValidationMsg field="desc" />
            </div>
          </div>

          {/* Nome do Agente */}
          <div style={{
            background: T.bgCard, border: `1px solid ${T.border}`,
            borderRadius: 14, padding: 20, marginBottom: 20,
          }}>
 <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 14 }}>Nome do Agente</div>
            <div>
              <label style={{ fontSize: 12, color: T.textMuted, display: 'block', marginBottom: 4 }}>
                Nome do Agente *
              </label>
              <Input
                value={cfg.agentName || ''}
                onChange={(v) => {
                  editConfig.value = { ...cfg, agentName: v };
                }}
                placeholder="Ex: Elon Maluco, Steve Jobs, IA Assistente..."
                style={INPUT_STYLE}
              />
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>
                Nome do agente que representa este cérebro. Será usado como identidade do agente na máquina.
              </div>
            </div>
          </div>

          {/* Config tabs */}
          <div style={{
            background: T.bgCard, border: `1px solid ${T.border}`,
            borderRadius: 14, padding: 20,
          }}>
 <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 14 }}>Arquivos de Configuração</div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
              {CONFIG_FIELDS.map(field => {
                const info = CONFIG_LABELS[field];
                const isActive = activeTab.value === field;
                return (
                  <Button variant="ghost"
                    key={field}
                    onClick={() => activeTab.value = field}
                    style={{
                      padding: '6px 12px', borderRadius: 8, border: 'none',
                      background: isActive ? T.primaryBg : T.bgCard,
                      color: isActive ? T.primary : T.textMuted,
                      fontSize: 12, fontWeight: isActive ? 700 : 500, cursor: 'pointer',
                    }}
                  >
                    {info?.emoji} {info?.label}
                  </Button>
                );
              })}
            </div>

            <Textarea
              value={cfg[activeTab.value]}
              onInput={(e: any) => {
                editConfig.value = { ...cfg, [activeTab.value]: e.target.value };
              }}
              rows={40}
              style={{
                width: '100%', padding: '14px 16px', borderRadius: 10,
                background: T.bg, border: `1px solid ${T.borderHover}`,
                color: T.text, fontSize: 13, fontFamily: 'monospace',
                lineHeight: '1.6', outline: 'none', resize: 'vertical',
              }}
              placeholder={`Conteúdo do arquivo ${CONFIG_LABELS[activeTab.value]?.label || activeTab.value}...`}
            />
          </div>
        </div>

        {/* ─── FOOTER (sticky, always visible) ─── */}
        <div style={{
          flexShrink: 0, padding: '14px 24px',
          borderTop: `1px solid ${T.bgHover}`,
          background: T.bgCard,
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          {/* Status messages */}
          {successMsg.value && (
            <div style={{
              padding: '10px 14px', borderRadius: 8,
              background: T.successBg, border: `1px solid ${T.successBg}`,
              fontSize: 12, fontWeight: 600, color: T.success,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
 {successMsg.value}
            </div>
          )}
          {error.value && (
            <div style={{
              padding: '10px 14px', borderRadius: 8,
              background: T.dangerBg, border: `1px solid ${T.dangerBg}`,
              fontSize: 12, fontWeight: 600, color: T.danger,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
 {error.value}
            </div>
          )}

          {/* Action buttons */}
          <div className="cerebro-card-actions" style={{ display: 'flex', gap: 12 }}>
            <Button variant="ghost"
              onClick={saveCerebro}
              disabled={!hasChanges || saving.value}
              style={{
                flex: 2, padding: '14px 24px', borderRadius: 12,
                border: hasChanges && !saving.value ? `2px solid ${T.success}` : `1px solid ${T.accentBg}`,
                background: hasChanges && !saving.value ? 'linear-gradient(135deg, T.successBg, T.successBg)' : T.accentBg,
                color: hasChanges && !saving.value ? T.success : T.textMuted,
                fontSize: 15, fontWeight: 700,
                cursor: hasChanges && !saving.value ? 'pointer' : 'not-allowed',
                opacity: hasChanges && !saving.value ? 1 : 0.5,
                transition: 'all 0.2s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              {saving.value ? (
                <>
                  <Spinner size="sm" inline /> Salvando...
                </>
              ) : (
                <>Salvar Alterações</>
              )}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
 successMsg.value = 'Configurações sincronizadas com o orquestrador!';
                setTimeout(() => successMsg.value = '', 4000);
              }}
              style={{
                padding: '14px 20px', borderRadius: 12,
              }}
            >Sincronizado</Button>
            <Button
              variant="danger"
              onClick={() => deleteCerebro(c.id)}
              style={{
                padding: '14px 20px', borderRadius: 12,
              }}
            >Deletar</Button>
          </div>
        </div>
      </div>
    );
  }

  // ── List mode ──
  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <div className="cerebro-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
 <h1 style={{ fontSize: 24, fontWeight: 800, color: T.text, margin: 0 }}>Admin — Cerebros</h1>
          <p style={{ fontSize: 13, color: T.textMuted, margin: '4px 0 0' }}>
            Gerencie personalidades e configurações de IA • {filteredCerebros.value.length} de {cerebros.value.length}
          </p>
        </div>
        <Button variant="primary" onClick={() => { showCreate.value = !showCreate.value; validationErrors.value = {}; }}>
          {showCreate.value ? 'Fechar' : '+ Novo Cerebro'}
        </Button>
      </div>

      {/* Filters + Search */}
      <div className="cerebro-filter-row" style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <Input
          placeholder="Buscar por nome, slug ou descrição..."
          value={searchQuery.value}
          onChange={(v) => searchQuery.value = v}
          style={{
            flex: 1, padding: '9px 14px', borderRadius: 8,
            background: T.blackAlpha30, border: `1px solid ${T.borderHover}`,
            color: T.text, fontSize: 13, outline: 'none',
          }}
        />
        <div style={{ display: 'flex', gap: 4 }}>
 {([['all', 'Todos'], ['active', 'Ativos'], ['inactive', 'Inativos']] as const).map(([val, label]) => (
            <Button variant="ghost"
              key={val}
              onClick={() => filterStatus.value = val}
              style={{
                padding: '7px 12px', borderRadius: 8, border: 'none',
                background: filterStatus.value === val ? T.primaryBg : T.bgCard,
                color: filterStatus.value === val ? T.primary : T.textMuted,
                fontSize: 12, fontWeight: filterStatus.value === val ? 700 : 500, cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >{label}</Button>
          ))}
        </div>
      </div>

      {successMsg.value && (
        <div style={{ background: T.successBg, border: `1px solid ${T.successBg}`, borderRadius: 10, padding: 12, marginBottom: 16, color: T.success, fontSize: 13 }}>
 {successMsg.value}
        </div>
      )}
      {error.value && (
        <div style={{ background: T.dangerBg, border: `1px solid ${T.dangerBg}`, borderRadius: 10, padding: 12, marginBottom: 16, color: T.danger, fontSize: 13 }}>
 {error.value}
        </div>
      )}

      {/* Create form */}
      {showCreate.value && (
        <div style={{
          background: T.bgCard, border: `1px solid ${T.primaryBg}`,
          borderRadius: 14, padding: 20, marginBottom: 20,
          animation: 'cerebro-slide-down 0.3s ease',
          overflow: 'hidden',
        }}>
 <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 14 }}>Novo Cerebro</div>
          <div className="cerebro-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: T.textMuted, display: 'block', marginBottom: 4 }}>Nome *</label>
              <input
                ref={createNameRef}
                value={newName.value}
                onInput={(e: any) => {
                  newName.value = e.target.value;
                  newSlug.value = e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                }}
                placeholder="Ex: Steve Jobs"
                style={INPUT_STYLE}
              />
              <ValidationMsg field="name" />
            </div>
            <div>
              <label style={{ fontSize: 12, color: T.textMuted, display: 'block', marginBottom: 4 }}>Slug *</label>
              <Input
                value={newSlug.value}
                onChange={(v) => newSlug.value = v}
                placeholder="steve-jobs"
                style={INPUT_STYLE}
              />
              <ValidationMsg field="slug" />
            </div>
          </div>
          <div className="cerebro-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: T.textMuted, display: 'block', marginBottom: 4 }}>Emoji</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  value={newEmoji.value}
                  onInput={(e: any) => newEmoji.value = e.target.value}
 placeholder=""
                  maxLength={4}
                  style={{ ...INPUT_STYLE, width: 60, textAlign: 'center', fontSize: 20 }}
                />
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {EMOJI_SUGGESTIONS.slice(0, 8).map(em => (
                    <Button variant="ghost"
                      key={em}
                      onClick={() => newEmoji.value = em}
                      style={{
                        width: 28, height: 28, borderRadius: 6, border: 'none',
                        background: newEmoji.value === em ? T.primaryBg : T.bgCard,
                        cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >{em}</Button>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <label style={{ fontSize: 12, color: T.textMuted, display: 'block', marginBottom: 4 }}>Categoria</label>
              <Select
                value={newCategory.value}
                onChange={(v) => newCategory.value = v}
                options={CATEGORIES.map(cat => ({ value: cat, label: cat }))}
                style={{ ...INPUT_STYLE, cursor: 'pointer' }}
              />
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={{ fontSize: 12, color: T.textMuted, display: 'block', marginBottom: 4 }}>Descrição</label>
            <Input
              value={newDesc.value}
              onChange={(v) => newDesc.value = v}
              placeholder="Breve descrição do cerebro (mín. 10 caracteres)"
              style={INPUT_STYLE}
            />
            <ValidationMsg field="desc" />
          </div>
          <Button variant="primary" onClick={createCerebro} disabled={creating.value} loading={creating.value} style={{ marginTop: 14 }}>
            {creating.value ? 'Criando...' : 'Criar Cerebro'}
          </Button>
        </div>
      )}

      {/* Cerebros list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {paginatedCerebros.value.map((c, i) => (
          <div
            key={c.id}
            style={{
              background: T.bgCard, border: `1px solid ${T.border}`,
              borderRadius: 14, padding: 18, display: 'flex', alignItems: 'center', gap: 16,
              opacity: c.enabled ? 1 : 0.5,
              animation: 'cerebro-fade-in 0.3s ease',
              animationDelay: `${i * 0.05}s`,
              animationFillMode: 'backwards',
              position: 'relative',
            }}
          >
            {/* Inactive badge */}
            {!c.enabled && (
              <div style={{
                position: 'absolute', top: 8, right: 12,
                background: T.dangerBg, border: `1px solid ${T.dangerBg}`,
                borderRadius: 6, padding: '2px 8px', fontSize: 10, fontWeight: 700,
                color: T.danger, letterSpacing: '0.5px',
              }}>INATIVO</div>
            )}
            <div style={{
              width: 48, height: 48, borderRadius: 12,
              background: `linear-gradient(135deg, ${T.purple}, ${T.accent})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0,
 }}>{c.emoji || ''}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{c.name}</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>
 {c.slug} • {c.enabled ? 'Ativo' : 'Desativado'}
                {c.category && <span> • {c.category}</span>}
                {' '}• Criado: {new Date(c.created_at).toLocaleDateString()}
              </div>
              {c.description && (
                <div style={{ fontSize: 13, color: T.textSecondary, marginTop: 4 }}>{c.description}</div>
              )}
            </div>
            <div className="cerebro-card-actions" style={{ display: 'flex', gap: 8 }}>
              <Button variant={c.enabled ? 'ghost' : 'success'} size="xs" onClick={() => toggleEnabled(c)}>
                {c.enabled ? 'Desativar' : 'Ativar'}
              </Button>
              <Button variant="secondary" size="xs" onClick={() => loadCerebroDetail(c.id)}>Editar</Button>
              <Button variant="danger" size="xs" onClick={() => deleteCerebro(c.id)}></Button>
            </div>
          </div>
        ))}

        {filteredCerebros.value.length === 0 && cerebros.value.length > 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: T.textMuted }}>
            Nenhum cerebro encontrado com os filtros atuais.
          </div>
        )}

        {cerebros.value.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: T.textMuted }}>
            Nenhum cerebro cadastrado. Clique em "+ Novo Cerebro" para começar.
          </div>
        )}
      </div>

      <Pagination />
    </div>
  );
}
